import './i18n/autoTranslate';

import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, User, Phone, MapPin, ArrowRight, UserPlus, Check, X, Sparkles, Shield, Zap, Globe } from 'lucide-react';
import SpotlightCard from '../components/ui/SpotlightCard';
import AppBackground from '../components/ui/AppBackground.tsx';
import { Link, useNavigate } from 'react-router-dom';
import TextType from '../components/ui/TextType';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser } from '../features/auth/authThunks';
import { setUser, setToken } from '../features/auth/authSlice';
import '../components/styles/Loading.css';
import { motion } from 'framer-motion';
import { FaLinkedin } from "react-icons/fa6";
import SEO from './SEO';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export function SignUpPage() {
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.auth);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    country: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [loaderState, setLoaderState] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasLowercase: false,
    hasUppercase: false,
    hasSpecialChar: false
  });
  const [showRequirements, setShowRequirements] = useState(false);

  const validatePasswordRequirements = (password) => {
    return {
      minLength: password.length >= 10 && password.length <= 15,
      hasNumber: /\d/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasSpecialChar: /[#\[\]()@$&*!?|.\/\\+_-]/.test(password)
    };
  };

  useEffect(() => {
    if (formData.password) {
      setPasswordRequirements(validatePasswordRequirements(formData.password));
    }
  }, [formData.password]);

  useEffect(() => {
    const handleOAuthMessage = (event) => {
      if (event.origin !== API_URL) return;

      const { type, provider, token, user, error } = event.data;

      if (type === 'oauth_success') {
        console.log(`${provider} OAuth successful`);

        localStorage.setItem('access_token', token);
        localStorage.setItem('user', JSON.stringify(user));

        dispatch(setToken(token));
        dispatch(setUser(user));

        setLoaderState(false);

        const onboardingMissing =
          !user?.phone_number ||
          !user?.country ||
          !user?.resume_data?.optional?.targetRole;
        
        setTimeout(() => {
          navigate(
            onboardingMissing ? "/onboarding-additional-info" : "/dashboard-home",
            { replace: true }
          );
        }, 800);

      } else if (type === 'oauth_error') {
        console.error(`${provider} OAuth error:`, error);
        setLoaderState(false);
        setFormErrors({ submit: `${provider} authentication failed: ${error}` });
      }
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [dispatch, navigate, API_URL]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else {
      const requirements = validatePasswordRequirements(formData.password);
      const allMet = Object.values(requirements).every(requirement => requirement);

      if (!allMet) {
        errors.password = 'Password does not meet all requirements';
      }
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveAuthSession = ({ token, refreshToken, user }) => {
    if (token) {
      localStorage.setItem("access_token", token);
      dispatch(setToken(token));
    }
  
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
  
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      dispatch(setUser(user));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setLoaderState(true);

    const userData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      password: formData.password,
    };

    try {
      const result = await dispatch(registerUser(userData)).unwrap();
      console.log('Registration successful:', result);
      if (result.success) {
        saveAuthSession({
          token: result.token,
          refreshToken: result.refreshToken,
          user: result.user,
        });
      
        setTimeout(() => {
          setLoaderState(false);
          navigate("/onboarding-additional-info", { replace: true });
        }, 800);
      } else {
        setFormErrors({ submit: result.message || 'Registration failed' });
      }
    } catch (err) {
      console.error('Registration failed:', err);
      setLoaderState(false);
      setFormErrors({ submit: err.message || 'Registration failed' });
    }
  };


  const handleGoogleSignUp = () => {
    setLoaderState(true);
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      `${API_URL}/api/auth/google/login`,
      'Google Sign Up',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  const handleLinkedInSignUp = () => {
    setLoaderState(true);
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      `${API_URL}/api/auth/linkedin/login`,
      'LinkedIn Sign Up',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  };

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Australia', 'Germany',
    'France', 'India', 'Japan', 'Brazil', 'Mexico', 'South Africa', 'Other'
  ];

  const PasswordRequirementItem = ({ met, text }) => (
    <div className="flex items-center gap-2 transition-colors duration-200">
      {met ? (
        <Check size={14} className="text-emerald-400 flex-shrink-0" />
      ) : (
        <X size={14} className="text-rose-400 flex-shrink-0" />
      )}
      <span className={`text-xs ${met ? 'text-emerald-300' : 'text-white/55'}`}>
        {text}
      </span>
    </div>
  );

  const glassCard =
    "relative rounded-[28px] border border-white/10 bg-white/[0.04] backdrop-blur-2xl " +
    "shadow-[0_22px_70px_-40px_rgba(0,0,0,0.85)] overflow-hidden";

  const panelBase = "relative p-7 sm:p-10 lg:p-12";

  const inputBase =
    "w-full px-4 py-3.5 rounded-2xl backdrop-blur-sm " +
    "bg-white/[0.04] border border-white/10 text-white placeholder-white/40 " +
    "focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/35 focus:border-white/15 " +
    "transition-all duration-300";

  const subtleGrid =
    "absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]";

  return (
    <AppBackground>
      {/* Enhanced Loading Overlay */}
      <SEO
        title="Sign Up for InterviewGenieAI — Start Practicing AI Interviews"
        description="Create your free InterviewGenieAI account and start practicing realistic AI interviews with instant feedback, scoring, and progress tracking."
        canonical="https://interviewgenieai.com/signup"
      />
      {loaderState && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-[100000000]">
          <div className="loader-container flex flex-col items-center justify-center">
            <div className="relative">
              <div className="w-20 h-20 rounded-full border-4 border-transparent border-t-[#6C63FF] border-r-[#6C63FF] animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <UserPlus className="w-8 h-8 text-[#6C63FF] animate-pulse" />
              </div>
            </div>
            <p className="text-white mt-6 text-lg font-medium bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
              Creating your account...
            </p>
            <p className="text-white/60 mt-2 text-sm">Preparing your interview workspace</p>
          </div>
        </div>
      )}

      {/* Background (TechApp-ish) */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-28 -left-28 w-[560px] h-[560px] rounded-full bg-[#6C63FF]/12 blur-[90px]" />
        <div className="absolute -bottom-28 -right-28 w-[560px] h-[560px] rounded-full bg-[#39B6FF]/10 blur-[95px]" />
        <div className="absolute inset-0 bg-[radial-gradient(1200px_700px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
        <div className={subtleGrid} />
        <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:92px_92px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-black/70" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4 pt-24 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5 }}
          className={`max-w-6xl w-full ${loaderState ? 'blur-sm' : ''}`}
        >
          <SpotlightCard className={glassCard}>
            {/* Card chrome */}
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-[#6C63FF]/10 blur-[110px]" />
            <div className="absolute -bottom-24 left-1/3 w-[520px] h-[520px] rounded-full bg-[#39B6FF]/8 blur-[110px]" />

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left Panel - Registration Form */}
              <div className={`${panelBase} border-b lg:border-b-0 lg:border-r border-white/10`}>
                {/* Top micro-bar */}
                <div className="flex items-center justify-between mb-10">
                  <div className="inline-flex items-center gap-2">
                    <div className="h-9 w-9 rounded-2xl bg-white/8 border border-white/12 backdrop-blur-xl flex items-center justify-center">
                      <UserPlus className="h-4 w-4 text-white/85" />
                    </div>
                    <div className="leading-tight">
                      <div className="text-sm font-semibold tracking-tight text-white">Create account</div>
                      <div className="text-[11px] text-white/55 -mt-0.5">Start in under a minute</div>
                    </div>
                  </div>

                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/70">
                    <Shield size={14} className="text-[#6C63FF]" />
                    <span>Secure</span>
                  </div>
                </div>

                <div className="max-w-md mx-auto">
                  {/* Header */}
                  <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 mb-5">
                      <span className="h-px w-10 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                      <span className="text-[12px] font-semibold tracking-[0.22em] text-[#6C63FF] uppercase">
                        Join InterviewGenieAI
                      </span>
                      <span className="h-px w-10 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
                    </div>

                    <h1 className="text-[34px] sm:text-4xl font-semibold tracking-tight bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">
                      Create your account
                    </h1>
                    <p className="text-white/60 mt-2">
                      Your interview workspace is waiting
                    </p>
                  </div>

                  {/* OAuth Buttons */}
                  <div className="mb-8">
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={handleGoogleSignUp}
                        className="relative group p-3.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 hover:border-white/20 transition-all duration-300 flex items-center justify-center gap-3"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#6C63FF]/0 via-white/0 to-[#39B6FF]/0 group-hover:from-[#6C63FF]/8 group-hover:via-white/3 group-hover:to-[#39B6FF]/8 transition-all duration-500 rounded-2xl" />
                        <svg className="w-5 h-5 relative z-10" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        <span className="text-sm font-semibold text-white/90 relative z-10">Google</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleLinkedInSignUp}
                        className="relative group p-3.5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/8 hover:border-white/20 transition-all duration-300 flex items-center justify-center gap-3"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-[#0A66C2]/0 via-white/0 to-[#6C63FF]/0 group-hover:from-[#0A66C2]/10 group-hover:via-white/3 group-hover:to-[#6C63FF]/8 transition-all duration-500 rounded-2xl" />
                        <FaLinkedin className="w-5 h-5 relative z-10 text-[#0A66C2]" />
                        <span className="text-sm font-semibold text-white/90 relative z-10">LinkedIn</span>
                      </button>
                    </div>

                    <div className="flex items-center my-7">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                      <span className="px-4 text-xs tracking-wide text-white/45 uppercase">or email</span>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                    </div>
                  </div>

                  {/* Error Message */}
                  {formErrors.submit && (
                    <div className="mb-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/30 backdrop-blur-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <p className="text-sm text-red-200">{formErrors.submit}</p>
                      </div>
                    </div>
                  )}

                  {/* Registration Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-3">
                          <div className="flex items-center gap-2">
                            <User size={16} className="text-[#6C63FF]" />
                            <span>First Name</span>
                          </div>
                        </label>
                        <input
                          name="firstName"
                          type="text"
                          value={formData.firstName}
                          onChange={handleChange}
                          className={`${inputBase} ${formErrors.firstName ? 'border-red-500/50 ring-1 ring-red-500/20' : ''}`}
                          placeholder="John"
                        />
                        {formErrors.firstName && (
                          <p className="text-red-300 text-xs mt-2">{formErrors.firstName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-white/80 mb-3">
                          <div className="flex items-center gap-2">
                            <User size={16} className="text-[#6C63FF]" />
                            <span>Last Name</span>
                          </div>
                        </label>
                        <input
                          name="lastName"
                          type="text"
                          value={formData.lastName}
                          onChange={handleChange}
                          className={`${inputBase} ${formErrors.lastName ? 'border-red-500/50 ring-1 ring-red-500/20' : ''}`}
                          placeholder="Doe"
                        />
                        {formErrors.lastName && (
                          <p className="text-red-300 text-xs mt-2">{formErrors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-white/80 mb-3">
                        <div className="flex items-center gap-2">
                          <Mail size={16} className="text-[#6C63FF]" />
                          <span>Email Address</span>
                        </div>
                      </label>
                      <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`${inputBase} ${formErrors.email ? 'border-red-500/50 ring-1 ring-red-500/20' : ''}`}
                        placeholder="you@example.com"
                      />
                      {formErrors.email && (
                        <p className="text-red-300 text-xs mt-2">{formErrors.email}</p>
                      )}
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-semibold text-white/80 mb-3">
                        <div className="flex items-center gap-2">
                          <Lock size={16} className="text-[#6C63FF]" />
                          <span>Password</span>
                        </div>
                      </label>
                      <div className="relative">
                        <input
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={handleChange}
                          onFocus={() => setShowRequirements(true)}
                          onBlur={() => setTimeout(() => setShowRequirements(false), 200)}
                          className={`${inputBase} pr-12 ${formErrors.password ? 'border-red-500/50 ring-1 ring-red-500/20' : ''}`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-xl text-white/50 hover:text-white/90 hover:bg-white/5 transition-colors"
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>

                      {/* Password Requirements */}
                      {(showRequirements || formData.password) && (
                        <div className="mt-3 p-4 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-xl">
                          <p className="text-sm font-semibold text-white/80 mb-3">Password Requirements</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <PasswordRequirementItem met={passwordRequirements.minLength} text="10–15 characters" />
                            <PasswordRequirementItem met={passwordRequirements.hasNumber} text="1 or more numbers" />
                            <PasswordRequirementItem met={passwordRequirements.hasLowercase} text="1 or more lowercase" />
                            <PasswordRequirementItem met={passwordRequirements.hasUppercase} text="1 or more uppercase" />
                            <PasswordRequirementItem met={passwordRequirements.hasSpecialChar} text="1 special character" />
                          </div>
                          <p className="text-xs text-white/45 mt-3">
                            Special characters: #[]()@$&*!?|./\+_-
                          </p>
                        </div>
                      )}

                      {formErrors.password && (
                        <p className="text-red-300 text-xs mt-2">{formErrors.password}</p>
                      )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label className="block text-sm font-semibold text-white/80 mb-3">
                        <div className="flex items-center gap-2">
                          <Lock size={16} className="text-[#6C63FF]" />
                          <span>Confirm Password</span>
                        </div>
                      </label>
                      <div className="relative">
                        <input
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className={[
                            inputBase,
                            "pr-12",
                            formErrors.confirmPassword
                              ? "border-red-500/50 ring-1 ring-red-500/20"
                              : formData.confirmPassword && formData.password === formData.confirmPassword
                                ? "border-emerald-500/40 ring-1 ring-emerald-500/10"
                                : ""
                          ].join(" ")}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-xl text-white/50 hover:text-white/90 hover:bg-white/5 transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>

                      {formData.confirmPassword && (
                        <div className="mt-2 flex items-center gap-2">
                          {formData.password === formData.confirmPassword ? (
                            <>
                              <Check size={14} className="text-emerald-400" />
                              <span className="text-emerald-300 text-xs">Passwords match</span>
                            </>
                          ) : (
                            <>
                              <X size={14} className="text-rose-400" />
                              <span className="text-rose-300 text-xs">Passwords do not match</span>
                            </>
                          )}
                        </div>
                      )}

                      {formErrors.confirmPassword && (
                        <p className="text-red-300 text-xs mt-2">{formErrors.confirmPassword}</p>
                      )}
                    </div>

                    {/* Terms */}
                    <div className="flex items-start gap-3">
                      <label className="relative flex items-center cursor-pointer mt-0.5">
                        <input
                          type="checkbox"
                          name="agreeToTerms"
                          checked={formData.agreeToTerms}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <div
                          className={[
                            "w-5 h-5 rounded-lg border flex items-center justify-center transition-all duration-300",
                            formData.agreeToTerms
                              ? "bg-[#6C63FF] border-[#6C63FF] shadow-[0_0_0_4px_rgba(108,99,255,0.18)]"
                              : "bg-white/5 border-white/20 hover:border-white/30",
                          ].join(" ")}
                        >
                          {formData.agreeToTerms && <Check size={12} className="text-white" />}
                        </div>
                      </label>

                      <div className="flex-1">
                        <label htmlFor="agreeToTerms" className="text-sm text-white/75 cursor-pointer">
                          I agree to the{' '}
                          <a href="/terms-of-service" className="text-[#6C63FF] hover:text-[#8B7CFF] transition-colors">
                            Terms and Conditions
                          </a>{' '}
                          and{' '}
                          <a href="/privacy-policy" className="text-[#6C63FF] hover:text-[#8B7CFF] transition-colors">
                            Privacy Policy
                          </a>
                        </label>
                        {formErrors.agreeToTerms && (
                          <p className="text-red-300 text-xs mt-2">{formErrors.agreeToTerms}</p>
                        )}
                      </div>
                    </div>

                    {/* CTA */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={loading || loaderState}
                      style={{
                        boxShadow:
                          'inset 0 -4px 10px rgba(255, 255, 255, 0.22), 0 0 55px rgba(108, 99, 255, 0.18)'
                      }}
                      className="px-8 py-3.5 w-full group rounded-2xl text-white font-semibold transition-all duration-300 flex items-center justify-center gap-3 relative overflow-hidden
                                 bg-gradient-to-br from-[#8B7CFF] via-[#6C63FF] to-[#39B6FF]
                                 hover:shadow-[0_0_70px_rgba(108,99,255,0.25)]"
                    >
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(800px_260px_at_50%_120%,rgba(255,255,255,0.35),transparent_60%)]" />
                      <div className="absolute -bottom-1 left-[18%] w-[64%] h-4 rounded-full bg-white/60 blur-[12px] pointer-events-none" />

                      {loading || loaderState ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Creating Account...</span>
                        </>
                      ) : (
                        <>
                          <UserPlus size={18} />
                          <span>Create Account</span>
                          <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                        </>
                      )}
                    </motion.button>
                  </form>

                  {/* Login Link */}
                  <div className="mt-8 pt-8 border-t border-white/10 text-center">
                    <p className="text-white/60 text-sm">
                      Already have an account?{' '}
                      <Link
                        to="/login"
                        className="text-[#6C63FF] hover:text-[#8B7CFF] font-semibold transition-colors group inline-flex items-center gap-1"
                      >
                        Sign in now
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Panel - Benefits Showcase */}
              <div className={`${panelBase} relative`}>
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#6C63FF]/8 via-transparent to-[#39B6FF]/8" />
                  <div className="absolute -top-24 -right-24 h-[520px] w-[520px] rounded-full bg-[#6C63FF]/10 blur-[90px]" />
                  <div className="absolute -bottom-28 -left-28 h-[520px] w-[520px] rounded-full bg-[#39B6FF]/8 blur-[95px]" />
                  <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:30px_30px]" />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/25" />
                </div>

                <div className="relative h-full flex flex-col">
                  <div className="w-full flex justify-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/6 border border-white/10">
                      <Sparkles size={16} className="text-[#6C63FF]" />
                      <span className="text-sm font-semibold text-white/80">Unlock Your Potential</span>
                    </div>
                  </div>

                  <div className="mt-8 text-center">
                    <TextType
                      text={[
                        "Start your journey to interview success.",
                        "Join thousands who aced their dream interviews.",
                        "Your future career starts with InterviewGenieAI."
                      ]}
                      typingSpeed={60}
                      pauseDuration={2000}
                      showCursor={true}
                      cursorCharacter="_"
                      className="px-4 text-3xl lg:text-4xl font-semibold tracking-tight bg-gradient-to-r from-white via-white/85 to-white bg-clip-text text-transparent leading-tight"
                    />
                    <p className="mt-4 text-white/60 max-w-xl mx-auto leading-relaxed">
                      Premium interview practice that feels like the real thing — structured, focused, and measurable.
                    </p>
                  </div>

                  <div className="mt-10 space-y-4">
                    {[
                      { icon: Zap, title: "AI-Powered Practice", description: "Real-time feedback on your interview performance" },
                      { icon: Shield, title: "Confidence Building", description: "Reduce anxiety with realistic AI interviews" },
                      { icon: Globe, title: "Global Standards", description: "Prepare for interviews worldwide" }
                    ].map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.08 }}
                        className="p-4 rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-xl
                                   hover:bg-white/[0.06] hover:border-white/15 transition-all duration-300"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#6C63FF]/18 to-[#39B6FF]/14 border border-white/10 flex items-center justify-center flex-shrink-0">
                            <benefit.icon className="w-6 h-6 text-[#6C63FF]" />
                          </div>
                          <div className="text-left">
                            <h4 className="font-semibold text-white/90 mb-1">{benefit.title}</h4>
                            <p className="text-sm text-white/60">{benefit.description}</p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-auto pt-10">
                    <div className="flex items-center justify-center gap-6 text-sm text-white/55">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span>Trusted by 10K+ Users</span>
                      </div>
                      <div className="w-1 h-1 rounded-full bg-white/20" />
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
                        <span>95% Success Rate</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </SpotlightCard>
        </motion.div>
      </div>
    </AppBackground>
  );
}
